import React, { useEffect } from "react";
import { getDecryptedUserData } from "../../../helpers/bycrypt";
import { svg } from "../../../Assets/svg";
import { useNavigate, useParams } from "react-router-dom";
import {
  PagePermissionNameData,
  usePermissionsChecker,
} from "../../../helpers/permissionModule";
import NoPermissionPage from "../../Components/NoPermissionPage";
import { Progress, Tooltip, Select } from "antd";
import moment from "moment";
import dayjs from "dayjs";
import { ClockInHistoryTabsEnum } from "./ClockInHistoryWrapper";
import { RootState, useAppSelector, useAppDispatch } from "../../../store";
import { GetProfileAPI } from "../../../Auth/Redux/auth";
import FullScreenLoader from "../../Components/FullScreenLoader";
import "./allowance.css";
import { InfoCircleOutlined } from "@ant-design/icons";
import { ROLE, buildAllowanceYearOptions, getBusinessCurrentYear } from "../../../helpers/utils";

const Allowance = ({
  pagePermission = PagePermissionNameData?.CLOCK_IN_HISTORY,
  setActiveView,
}: any) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const userData = getDecryptedUserData();
  const USER_PERMISSIONS = usePermissionsChecker(pagePermission);

  const dispatch = useAppDispatch();
  const { authLoading } = useAppSelector((state: RootState) => state?.loading);

  const yearOptions = React.useMemo(() => buildAllowanceYearOptions(), []);
  const [selectedYear, setSelectedYear] = React.useState<number>(getBusinessCurrentYear());

  useEffect(() => {
    dispatch(GetProfileAPI(navigate, undefined, selectedYear));
  }, [dispatch, navigate, selectedYear]);

  const isAllowedToView = Boolean(
    userData?.isEmployee ||
    userData?.role?.key === "employee" ||
    String(userData?.role?.name || "").toLowerCase() === "employee" ||
    USER_PERMISSIONS?.READ ||
    userData?.role?.key === ROLE.ADMIN
  );

  if (!isAllowedToView) {
    return <NoPermissionPage />;
  }

  if (authLoading) {
    return (
      <div className="py-5 position-relative" style={{ minHeight: "300px" }}>
        <FullScreenLoader />
      </div>
    );
  }

  const totalUsedTimeOff = userData?.allowance?.pto || 0;

  const totalAllowances =
    (userData?.allowance?.paid_leave || 0) +
    (userData?.allowance?.carry_forward || 0);

  const remainingAllowances = Math.max(0, totalAllowances - totalUsedTimeOff);

  const availableAllowancePercentage =
    totalAllowances > 0
      ? Math.round((remainingAllowances * 100) / totalAllowances)
      : 0;

  return (
    <div className="allowance-container">
      <div className="page-heading mb-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div>
          <h1 className="text-black fs-36 fw-bolder page-title mb-0">Allowance</h1>
          <p className="mb-0">Your time off balance and details at a glance.</p>
        </div>
        <div style={{ minWidth: 190 }} className="whatsBannedDropdown filterCommonDropdown mw-100 d-inline-block">
          <Select
            value={selectedYear}
            onChange={(val) => setSelectedYear(val)}
            className="form-select form-select-lg"
            style={{ minWidth: 190 }}
            suffixIcon={svg?.selectDownArrow}
          >
            {yearOptions.map((opt) => (
              <Select.Option key={opt.value} value={opt.value}>
                {opt.label}
              </Select.Option>
            ))}
          </Select>
        </div>
      </div>

      <div className="row g-4">
        {/* Left Column: Summary Card */}
        <div className="col-xl-5 col-12">
          <div className="summary-card">
            {/* Antd Progress */}
            <Progress
              type="circle"
              percent={availableAllowancePercentage}
              size={300}
              strokeColor="#2563eb"
              format={() => (
                <div className="d-flex flex-column align-items-center justify-content-center text-center responsive-progress-text">
                  <div className="fw-bold text-dark lh-1 mb-1 val">
                    {remainingAllowances} Days
                  </div>
                  <div className="text-secondary fw-medium lh-sm mt-1 lbl" style={{ maxWidth: "120px" }}>
                    to use before<br />31 Dec {selectedYear}
                  </div>
                </div>
              )}
            />

            {/* Stat List */}
            <div className="stat-list">
              <div className="stat-row">
                <div className="stat-icon blue">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                </div>
                <div className="stat-text">
                  <div className="label d-flex align-items-center gap-1">Total Allowance <Tooltip
                    placement="bottomRight"
                    overlayStyle={{ maxWidth: "calc(100vw - 100px)", width: "max-content" }}
                    title={
                      "Total of Time Off Allowance and Carry Forward Allowance"
                    }
                  >
                    <span className="cursor-pointer text-secondary d-inline-flex align-items-center svg-14">
                      <InfoCircleOutlined />
                    </span>
                  </Tooltip></div>
                  <div className="value">{totalAllowances} Days</div>
                </div>
              </div>
              <div className="stat-row">
                <div className="stat-icon green">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>
                </div>
                <div className="stat-text">
                  <div className="label">Used</div>
                  <div className="value">{userData?.allowance?.pto || 0} Days</div>
                </div>
              </div>
              <div className="stat-row">
                <div className="stat-icon purple">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                </div>
                <div className="stat-text">
                  <div className="label">Remaining</div>
                  <div className="value">{remainingAllowances} Days</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: List Cards */}
        <div className="col-xl-7 col-12">
          <div className="list">
            <div className="list-card">
              <div className="list-icon blue">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2" /><path d="M15 18H9" /><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14" /><circle cx="17" cy="18" r="2" /><circle cx="7" cy="18" r="2" /></svg>
              </div>
              <div className="list-info">
                <div className="title">Carry Forward Allowance</div>
                <div className="desc">Unused days from previous year</div>
              </div>
              <div className="list-right">
                <div className="pill blue">{userData?.allowance?.carry_forward || 0} Days</div>
              </div>
            </div>

            <div className="list-card">
              <div className="list-icon purple">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
              </div>
              <div className="list-info">
                <div className="title">Used Time Off</div>
                <div className="desc">Used Paid Time Off Allowance</div>
              </div>
              <div className="list-right">
                <div className="pill purple">{userData?.allowance?.pto || 0} Days</div>
              </div>
            </div>

            <div className="list-card">
              <div className="list-icon orange">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
              </div>
              <div className="list-info">
                <div className="title">Time in Lieu</div>
                <div className="desc">Compensatory time off</div>
              </div>
              <div className="list-right">
                <div className="pill orange">{userData?.allowance?.time_in_lieu || 0} Hours</div>
              </div>
            </div>

            <div className="list-card cursor-pointer" onClick={() => {
              if (selectedYear) {
                sessionStorage.setItem("clock_in_time_off_history_year", String(selectedYear));
              }
              sessionStorage.setItem("clock_in_history_active_tab", ClockInHistoryTabsEnum.TIME_OFF_HISTORY);
              setActiveView(ClockInHistoryTabsEnum.TIME_OFF_HISTORY);
            }}>
              <div className="list-icon pink">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" /><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" /></svg>
              </div>
              <div className="list-info">
                <div className="title">Remaining Time Off Allowance</div>
                <div className="desc">Total available paid time off for this year</div>
              </div>
              <div className="list-right">
                <div className="pill pink">{remainingAllowances} Days</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Allowance;
